import { getEnumTranslationKey, useModelLabel } from "@judo/i18n";
import type { Filter } from "@judo/model-api";
import { Autocomplete, InputAdornment, MenuItem, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import type { ChangeEvent, ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconRenderer } from "./IconRenderer";
import { getFilterColumnType } from "../utils/filter-operator-utils";

export interface SingleFilterInputProps<T = unknown> {
	filter: Filter;
	value: T;
	onChange: (value: T | null) => void;
	testId: string;
	size?: "small" | "medium";
}

const DEBOUNCE_MS = 500;

// ─────────────────────────────────────────────────────────────────────────────
// Review refactor R3 — shared filter-input primitives
//
// All six filter sub-components consume the same prop shape and compose the
// same `TextField slotProps` envelope. The `BaseFilterInputProps` type and
// `filterSlotProps()` helper below let each variant express only what's
// unique to its underlying widget.
// ─────────────────────────────────────────────────────────────────────────────

interface BaseFilterInputProps<T> {
	label: string;
	value: T;
	onChange: (value: T | null) => void;
	iconAdornment?: ReactNode;
	testId: string;
	size?: "small" | "medium";
}

/**
 * Shared `slotProps.textField` envelope for every MUI X picker used as a
 * filter input. The shape matches what the pickers' `slotProps.textField`
 * and a bare `<TextField>` both accept, so each variant can spread it as-is.
 */
interface FilterTextFieldProps {
	variant: "standard";
	size: "small" | "medium" | undefined;
	fullWidth: true;
	slotProps: {
		input: { startAdornment: ReactNode } | undefined;
	};
}

function filterTextFieldProps(
	iconAdornment: ReactNode | undefined,
	size: "small" | "medium" | undefined
): FilterTextFieldProps {
	return {
		variant: "standard",
		size,
		fullWidth: true,
		slotProps: {
			input: iconAdornment ? { startAdornment: iconAdornment } : undefined,
		},
	};
}

/** Normalize tri-state boolean filter value → MUI Select string. */
function boolToSelectValue(value: unknown): "" | "true" | "false" {
	if (value === true) return "true";
	if (value === false) return "false";
	return "";
}

/**
 * Parse a filter value to a Date. Accepts Date, ISO string, or falsy → null.
 */
function parseFilterDate(value: unknown): Date | null {
	if (value instanceof Date) return value;
	if (!value) return null;
	const parsed = new Date(value as string);
	return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Renders a single filter input based on the filter's data type.
 * String and numeric inputs are debounced (500ms). Other types fire immediately.
 */
export function SingleFilterInput<T = unknown>({
	filter,
	value,
	onChange,
	testId,
	size = "medium",
}: SingleFilterInputProps<T>) {
	const { label } = useModelLabel(filter);
	const columnType = getFilterColumnType(filter);
	const filterTestId = `${testId}::fixed-filter::${filter.attributeType?.name ?? filter.name}`;

	const iconAdornment = filter.icon ? (
		<InputAdornment position="start">
			<IconRenderer icon={filter.icon} size="small" />
		</InputAdornment>
	) : undefined;

	// Each variant is pinned to its own concrete value type. The dispatcher sits
	// at the boundary where the runtime `columnType` selects the variant, so we
	// build a freshly-typed `base` per case rather than sharing one across all
	// branches — this keeps the generic narrowing honest without `as` casts.
	switch (columnType) {
		case "string": {
			const base: BaseFilterInputProps<string | number> = {
				label,
				value: value as string | number,
				onChange: onChange as (v: (string | number) | null) => void,
				iconAdornment,
				testId: filterTestId,
				size,
			};
			return <DebouncedTextFilter {...base} />;
		}
		case "number": {
			const base: BaseFilterInputProps<string | number> = {
				label,
				value: value as string | number,
				onChange: onChange as (v: (string | number) | null) => void,
				iconAdornment,
				testId: filterTestId,
				size,
			};
			return <DebouncedTextFilter {...base} type="number" />;
		}
		case "boolean": {
			const base: BaseFilterInputProps<boolean> = {
				label,
				value: value as boolean,
				onChange: onChange as (v: boolean | null) => void,
				iconAdornment,
				testId: filterTestId,
				size,
			};
			return <BooleanFilterInput {...base} />;
		}
		case "date": {
			const base: BaseFilterInputProps<Date> = {
				label,
				value: value as Date,
				onChange: onChange as (v: Date | null) => void,
				iconAdornment,
				testId: filterTestId,
				size,
			};
			return <DateFilterInput {...base} />;
		}
		case "dateTime": {
			const base: BaseFilterInputProps<Date> = {
				label,
				value: value as Date,
				onChange: onChange as (v: Date | null) => void,
				iconAdornment,
				testId: filterTestId,
				size,
			};
			return <DateTimeFilterInput {...base} />;
		}
		case "singleSelect": {
			const base: BaseFilterInputProps<string> = {
				label,
				value: value as string,
				onChange: onChange as (v: string | null) => void,
				iconAdornment,
				testId: filterTestId,
				size,
			};
			return <EnumerationFilterInput {...base} filter={filter} />;
		}
		default: {
			const base: BaseFilterInputProps<string | number> = {
				label,
				value: value as string | number,
				onChange: onChange as (v: (string | number) | null) => void,
				iconAdornment,
				testId: filterTestId,
				size,
			};
			return <DebouncedTextFilter {...base} />;
		}
	}
}

// ─────────────────────────────────────────────────────────────────────────────
// Variants
// ─────────────────────────────────────────────────────────────────────────────

/** Debounced TextField for string and numeric filters. */
function DebouncedTextFilter({
	label,
	value,
	onChange,
	iconAdornment,
	testId,
	size,
	type,
}: BaseFilterInputProps<string | number> & { type?: string }) {
	const [localValue, setLocalValue] = useState<string>((value as string) ?? "");
	const onChangeRef = useRef(onChange);
	onChangeRef.current = onChange;

	useEffect(() => {
		setLocalValue((value as string) ?? "");
	}, [value]);

	useEffect(() => {
		const timer = setTimeout(() => {
			const emitted = type === "number" && localValue !== "" ? Number(localValue) : localValue || null;
			onChangeRef.current(emitted);
		}, DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [localValue, type]);

	const fieldProps = filterTextFieldProps(iconAdornment, size);

	return (
		<TextField
			{...fieldProps}
			label={label}
			value={localValue}
			onChange={(e) => setLocalValue(e.target.value)}
			type={type}
			data-testid={testId}
		/>
	);
}

/** Boolean filter with three states: blank (no filter), true, false. */
function BooleanFilterInput({ label, value, onChange, iconAdornment, testId, size }: BaseFilterInputProps<boolean>) {
	const { t } = useTranslation();

	const handleChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const val = e.target.value;
			onChange(val === "" ? null : val === "true");
		},
		[onChange]
	);

	const fieldProps = filterTextFieldProps(iconAdornment, size);

	return (
		<TextField
			{...fieldProps}
			select
			label={label}
			value={boolToSelectValue(value)}
			onChange={handleChange}
			data-testid={testId}
		>
			<MenuItem value="">{t("judo.filter.boolean.any", { defaultValue: "" })}</MenuItem>
			<MenuItem value="true">{t("judo.filter.boolean.true", { defaultValue: "True" })}</MenuItem>
			<MenuItem value="false">{t("judo.filter.boolean.false", { defaultValue: "False" })}</MenuItem>
		</TextField>
	);
}

/** Date filter using MUI DatePicker. */
function DateFilterInput({ label, value, onChange, iconAdornment, testId, size }: BaseFilterInputProps<Date>) {
	const textField = filterTextFieldProps(iconAdornment, size);
	return (
		<div data-testid={testId}>
			<DatePicker
				label={label}
				value={parseFilterDate(value)}
				onChange={(newValue) => onChange(newValue)}
				slotProps={{ textField }}
			/>
		</div>
	);
}

/** DateTime filter using MUI DateTimePicker. */
function DateTimeFilterInput({ label, value, onChange, iconAdornment, testId, size }: BaseFilterInputProps<Date>) {
	const textField = filterTextFieldProps(iconAdornment, size);
	return (
		<div data-testid={testId}>
			<DateTimePicker
				label={label}
				value={parseFilterDate(value)}
				onChange={(newValue) => onChange(newValue)}
				ampm={false}
				slotProps={{ textField }}
			/>
		</div>
	);
}

/** Enumeration filter using MUI Autocomplete. */
function EnumerationFilterInput({
	filter,
	label,
	value,
	onChange,
	iconAdornment,
	testId,
	size,
}: BaseFilterInputProps<string> & { filter: Filter }) {
	const { t } = useTranslation();
	const enumType = filter.attributeType?.dataType as
		| { name?: string; members?: Array<{ name: string; ordinal: number; label?: string }> }
		| undefined;
	// Translate each member name through the standard `enum.{enumName}.{value}`
	// key (see `getEnumTranslationKey`). Falls back to `member.label`, then to
	// the raw `member.name` when no translation is wired up — same precedence
	// as the input-side `convertEnumOptions()` in InputRenderer.
	const enumName = enumType?.name ?? "";
	const options =
		enumType?.members?.map((member) => ({
			value: member.name,
			label: t(getEnumTranslationKey(enumName, member.name), {
				defaultValue: member.label ?? member.name,
			}),
		})) ?? [];

	const selectedOption = options.find((opt) => opt.value === value) ?? null;

	return (
		<Autocomplete
			size={size}
			value={selectedOption}
			onChange={(_, newValue) => onChange(newValue?.value ?? null)}
			options={options}
			getOptionLabel={(option) => option.label}
			isOptionEqualToValue={(option, val) => option.value === val.value}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label}
					variant="standard"
					data-testid={testId}
					slotProps={{
						input: {
							...params.InputProps,
							...(iconAdornment ? { startAdornment: iconAdornment } : {}),
						},
					}}
				/>
			)}
			fullWidth
		/>
	);
}
